import type { AutomationType } from '@playbook-technologies/common-enums';
import { useState } from 'react';

import type { Async } from '@client/new/helpers/types';

import { AutomationLogEntry, AutomationStatsEntry } from '../automations.api';
import { AutomationLogsFilter } from '../automations.keys';
import {
  AutomationInsightsError,
  useAutomationLogsModel,
  useAutomationStatsModel,
} from '../model/AutomationInsights.model';
import { AutomationsRequestErrorKind } from '../model/Automations.model';

const INSIGHTS_ERROR_MESSAGES: Record<AutomationsRequestErrorKind, string> = {
  unauthorized: 'Your session has expired. Sign in again to view automations.',
  forbidden: "You don't have permission to view automation data.",
  network: "We couldn't load automation data.",
};

type InsightsViewModelError = {
  message: string;
  retry: () => Promise<void>;
};

const toViewModelError = (
  error: AutomationInsightsError,
): InsightsViewModelError => ({
  message: INSIGHTS_ERROR_MESSAGES[error.kind],
  retry: error.retry,
});

const wholeDollars = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
});

const formatCents = (cents: number): string => wholeDollars.format(cents / 100);

const formatDateTime = (iso: string | null): string =>
  iso ? new Date(iso).toLocaleString() : '—';

type StatsCardViewModel = {
  automationType: string;
  title: string;
  triggerLine: string;
  headline: { label: string; value: string; hint: string }[];
  steps: AutomationStatsEntry['steps'];
  discrepancy: AutomationStatsEntry['discrepancy'];
  drainingLine: string | null;
};

type StatsViewModel = { cards: StatsCardViewModel[] };

const toDrainingLine = (entry: AutomationStatsEntry): string | null => {
  const draining = (entry.versions ?? [])
    .filter((version) => !version.isCurrent)
    .reduce((sum, version) => sum + version.active, 0);
  return draining > 0 ? `Previous version, draining: ${draining}` : null;
};

const toStatsCard = (entry: AutomationStatsEntry): StatsCardViewModel => ({
  automationType: entry.automationType,
  title: entry.displayName,
  triggerLine: entry.triggerLine,
  headline: [
    {
      label: 'Enrolled',
      value: String(entry.enrolled),
      hint: 'Everyone who ever entered this flow, both active and closed enrollments.',
    },
    {
      label: 'Converted',
      value: String(entry.converted),
      hint: 'Enrollments that closed because the person completed the goal, e.g. finished the purchase.',
    },
    {
      label: 'Recovered to date',
      value: formatCents(entry.recoveredToDate),
      hint: 'Net revenue from conversions that happened within 7 days of the last email sent before the purchase.',
    },
    {
      label: 'Influenced GMV',
      value: formatCents(entry.influencedGmv),
      hint: 'Net revenue from every conversion preceded by at least one email from this flow, with no time limit. Never smaller than recovered to date.',
    },
  ],
  steps: entry.steps,
  discrepancy: entry.discrepancy,
  drainingLine: toDrainingLine(entry),
});

export const useAutomationStatsViewModel = (
  creatorId: number,
): Async<StatsViewModel, InsightsViewModelError> => {
  const model = useAutomationStatsModel(creatorId);
  if (model.type === 'fetch-error') {
    return { type: 'fetch-error', value: toViewModelError(model.value) };
  }
  if (model.type !== 'fetched') return model;
  return {
    type: 'fetched',
    value: { cards: model.value.stats.map(toStatsCard) },
  };
};

export type LogStepRow = {
  stepNo: number;
  sentAt: string;
  openedAt: string;
  clickedAt: string;
};

export type LogRow = {
  id: string;
  email: string;
  trigger: string;
  progress: string;
  status: string;
  firstSentAt: string;
  steps: LogStepRow[];
};

/** No `status` on the wire: open logs have `closedAt === null`, closed ones
 * carry the outcome in `closeReason` (a server key, humanized here). */
const toStatus = (entry: AutomationLogEntry): string => {
  if (entry.closedAt === null) return 'Active';
  return entry.closeReason ? entry.closeReason.replace(/_/g, ' ') : 'Closed';
};

const toLogRow = (entry: AutomationLogEntry): LogRow => ({
  id: entry.id,
  email: entry.email,
  trigger: entry.triggerKey,
  progress: `${entry.completedStepCount}/${entry.stepCount}`,
  status: toStatus(entry),
  firstSentAt: formatDateTime(entry.steps[0]?.sentAt ?? null),
  steps: entry.steps.map((step) => ({
    stepNo: step.stepNo,
    sentAt: formatDateTime(step.sentAt),
    openedAt: formatDateTime(step.openedAt),
    clickedAt: formatDateTime(step.clickedAt),
  })),
});

type LogsViewModel = {
  rows: LogRow[];
  total: number;
  isRefreshing: boolean;
  page: number;
  perPage: number;
  emailInput: string;
  automationType: AutomationType | '';
  setPage: (page: number) => void;
  setPerPage: (perPage: number) => void;
  setEmailInput: (value: string) => void;
  applyEmailFilter: () => void;
  setAutomationType: (automationType: AutomationType | '') => void;
};

export const PER_PAGE_OPTIONS = [20, 50, 100] as const;

export const useAutomationLogsViewModel = (
  creatorId: number,
): Async<LogsViewModel, InsightsViewModelError> => {
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState<number>(PER_PAGE_OPTIONS[0]);
  const [emailInput, setEmailInput] = useState('');
  const [appliedEmail, setAppliedEmail] = useState('');
  const [automationType, setAutomationType] = useState<AutomationType | ''>('');

  const filter: AutomationLogsFilter = {
    ...(automationType ? { automationType } : {}),
    ...(appliedEmail ? { email: appliedEmail } : {}),
    page: page + 1,
    perPage,
  };
  const model = useAutomationLogsModel(creatorId, filter);

  if (model.type === 'fetch-error') {
    return { type: 'fetch-error', value: toViewModelError(model.value) };
  }
  if (model.type !== 'fetched') return model;

  return {
    type: 'fetched',
    value: {
      rows: model.value.entries.map(toLogRow),
      total: model.value.total,
      isRefreshing: model.value.isRefreshing,
      page,
      perPage,
      emailInput,
      automationType,
      setPage,
      setPerPage: (next) => {
        setPerPage(next);
        setPage(0);
      },
      setEmailInput,
      applyEmailFilter: () => {
        setAppliedEmail(emailInput.trim());
        setPage(0);
      },
      setAutomationType: (next) => {
        setAutomationType(next);
        setPage(0);
      },
    },
  };
};
